import LoadingButton from "@mui/lab/LoadingButton";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDarkMode } from "../useDarkMode";
import {
  getCurrentStatus,
  getIsWorkflowSaving,
  getIsWorkflowSaved,
  getWorkflowData,
  selectValidationResults,
  selectAIQCLimit,
  selectCallType,
  validateTranscript,
} from "store/editor";
import SaveWorkflowModal from "../saveWorkflowModal";
import CheckInProgressModal from "../aiQualityCheck/modal";
import CoverageWarningModal from "../components/coverageWarningModal";
import IntegrityFailureModal from "../components/integrityFailureModal";
import {
  loadResolvedSet,
  makeIssueKey,
  removeResolvedSet,
} from "../aiQualityCheck/helpers";
import { isUserApplicant, isUserAdmin, getUserInfo } from "utils/userHelpers";
import { showToast } from "store/toastSlice";
import { getCoverageData, removeCoverage, getTotalPlayed } from "../hooks/useEditorCoverageTracker";
import { sendCoverageWarningNotification } from "utils/enterpriseApi";
import { getPartnerCodeName } from "pages/enterpriseDashboard/utils";

export const MAX_AI_QC_ATTEMPTS = 5;
export const TRIGGERS = {
  APPLICANT: {
    AI_QC: null, // "Compliance", disabled for applicants on staging
    SPEAKER_VERIFICATION: null, // "Compliance", disabled for applicants on staging
  },

  // For first draft final files, trigger AI QC and Speaker Verification on QA_Review
  APPLICANT_FDF: {
    AI_QC: null, // "QA_Review", disabled for applicants on staging
    SPEAKER_VERIFICATION: null, // "QA_Review", disabled for applicants on staging
  },

  EDITOR: {
    AI_QC: "QA_Review",
    SPEAKER_VERIFICATION: "First_Review",
  },

  // For first draft final files, trigger AI QC on QA_Review and Speaker Verification on First_Review
  EDITOR_FDF: {
    AI_QC: "QA_Review",
    SPEAKER_VERIFICATION: "First_Review",
  },
};

export default function NavButtons({ setOpenQcDrawer, readOnly }) {
  const dispatch = useDispatch();
  const isApplicant = isUserApplicant();
  const isDarkMode = useDarkMode();
  const shouldAutoRender = useRef(true);

  const workflowData = useSelector(getWorkflowData);
  const workflowId = workflowData?.id ?? null;
  const workflowStatus = useSelector(getCurrentStatus);
  const isSaving = useSelector(getIsWorkflowSaving);
  const isSaved = useSelector(getIsWorkflowSaved);
  const validationResults = useSelector(selectValidationResults);
  const aiQCLimit = useSelector(selectAIQCLimit);
  const callType = useSelector(selectCallType);

  const [openSaveModal, setOpenSaveModal] = useState(false);
  const [speakerVerified, setSpeakerVerified] = useState(false);
  const [qcLoading, setQcLoading] = useState(false);
  const [qcTriggered, setQCTriggered] = useState(false);
  const [showCoverageWarningModal, setShowCoverageWarningModal] = useState(false);
  const [showIntegrityFailureModal, setShowIntegrityFailureModal] = useState(false);
  const [, setCoverageDataRef] = useState(null);
  const isExpertCall = (callType === "expert call"); // Expert calls don't require speaker verification
  let aiQCTrigger = (isApplicant && workflowStatus === TRIGGERS.APPLICANT.AI_QC) ||
    (isApplicant === false && workflowStatus === TRIGGERS.EDITOR.AI_QC);
  let speakerVerificationTrigger = (isApplicant && workflowStatus === TRIGGERS.APPLICANT.SPEAKER_VERIFICATION) ||
    (isApplicant === false && workflowStatus === TRIGGERS.EDITOR.SPEAKER_VERIFICATION && isExpertCall === false);

  if (workflowData?.upload_type === "FIRST_DRAFT") {
    if (isApplicant) {
      aiQCTrigger = (workflowStatus === TRIGGERS.APPLICANT_FDF.AI_QC);
      speakerVerificationTrigger = (workflowStatus === TRIGGERS.APPLICANT_FDF.SPEAKER_VERIFICATION);
    } else {
      aiQCTrigger = (workflowStatus === TRIGGERS.EDITOR_FDF.AI_QC);
      speakerVerificationTrigger = (workflowStatus === TRIGGERS.EDITOR_FDF.SPEAKER_VERIFICATION && isExpertCall === false);
    }
  }

  const qcCallback = useCallback((loading) => {
    setOpenQcDrawer(!loading);
    setQcLoading(loading);
  }, [setOpenQcDrawer]);

  const checkTranscriptQuality = useCallback(() => {
    // Clear resolved issues for this workflow when starting a new quality check
    removeResolvedSet(workflowId);
    qcCallback(true);
    dispatch(validateTranscript({ callback: () => qcCallback(false) }));
  }, [dispatch, qcCallback, workflowId]);

  const onCompleteClick = useCallback(() => {
    // Only run AI QC for applicants on the Compliance stage
    // For everyone else, run AI QC always
    if (aiQCTrigger) {
      // First complete click: trigger AI validation only
      if (!validationResults && aiQCLimit < MAX_AI_QC_ATTEMPTS) {
        checkTranscriptQuality();
        return;
      } else {
        if (!validationResults && aiQCLimit >= MAX_AI_QC_ATTEMPTS) {
          dispatch(
            showToast({
              message: "QA Quality Check maximum number of attempts reached, skipping...",
              severity: "warning",
            }),
          );
        }
      }

      const resolvedSet = loadResolvedSet(workflowId);
      const unresolvedCount = (validationResults || []).filter((item) => {
        const key = makeIssueKey({
          category: item.category,
          entity: item.entity,
          startTime: item.start_time,
        });

        return !resolvedSet.has(key);
      }).length;

      if (unresolvedCount > 0) {
        if (setOpenQcDrawer) setOpenQcDrawer(true);
        dispatch(
          showToast({
            message: `Please address ${unresolvedCount} validation issue${unresolvedCount > 1 ? "s" : ""
              } before completing`,
            severity: "error",
            vertical: "top",
          })
        );

        return;
      }
    }

    if (setOpenSaveModal) {
      setOpenSaveModal(true);
    }
  }, [dispatch, aiQCLimit, aiQCTrigger, validationResults, workflowId, checkTranscriptQuality, setOpenQcDrawer]);

  // Continues the complete flow after coverage check passes
  const proceedAfterCoverage = useCallback(() => {
    if (!speakerVerified && speakerVerificationTrigger) {
      const event = new CustomEvent("editor.speakers.open", {
        detail: {
          showButton: true,
          automaticCheck: true,
        },
      });

      dispatchEvent(event);

      return;
    } else {
      onCompleteClick();
    }
  }, [speakerVerified, speakerVerificationTrigger, onCompleteClick]);

  const onCoverageWarningSubmit = useCallback(() => {
    setShowCoverageWarningModal(false);
    setCoverageDataRef(null);

    // Coverage data is NOT removed here — it's cleaned up only after updateWorkflow succeeds
    proceedAfterCoverage();
  }, [proceedAfterCoverage]);

  const onCompleteCheck = useCallback(() => {
    // Coverage check runs first, before speaker validation
    const admin = isUserAdmin();
    const applicant = isUserApplicant();

    if (!admin && !applicant) {
      window.dispatchEvent(new CustomEvent("coverage:setIntegrity"));

      const coverage = getCoverageData(workflowData?.id, workflowStatus);

      if (coverage) {
        coverage.workflowTitle = workflowData?.title || null;
        coverage.workflowStatus = workflowStatus;
        coverage.uploadType = workflowData?.upload_type || null;
        // editCount is already set by the coverage tracker hook as delta (version - initialVersion)
        coverage.durationSeconds = workflowData?.duration_seconds || 0;
        coverage.jobType = workflowData?.job_type || null;
        coverage.partnerName = getPartnerCodeName(workflowData?.assignees) || null;
        const userInfo = getUserInfo();
        coverage.team = userInfo?.user?.team || null;

        const totalPlayed = getTotalPlayed(coverage.playedSegments);
        const audioPercent = coverage.audioDuration > 0
          ? (totalPlayed / coverage.audioDuration) * 100
          : 0;
        const viewedCount = Math.min(coverage.viewedSnippetIds.length, coverage.totalSnippetCount);
        const scrollPercent = coverage.totalSnippetCount > 0
          ? (viewedCount / coverage.totalSnippetCount) * 100
          : 0;

        // Always send coverage data to backend (backend decides Slack)
        sendCoverageWarningNotification(coverage).catch((e) =>
          console.error("Coverage notification error:", e)
        );

        // Show warning modal for suspicious activity
        const lowAudio = workflowStatus === "First_Review"
          ? audioPercent < 50
          : audioPercent < 85;
        const lowScroll = scrollPercent < 85;
        const editsInStage = coverage.editCount || 0;
        const lowEdits = workflowStatus === "QA_Review" && editsInStage < 40;
        const integrityFailed = coverage.integrity_check === false;
        const reassignEnabled = process.env.REACT_APP_INTEGRITY_FAIL_REASSIGN_ENABLED === "true";

        if (reassignEnabled && integrityFailed) {
          // If paragraph integrity check fails, show warning modal and exit to dashboard immediately.
          // This only happens if file re-assignment is enabled
          setShowIntegrityFailureModal(true);

          return;
        }

        if (lowAudio || lowScroll || lowEdits || (!reassignEnabled && integrityFailed)) {
          // When immediate file reassignment is disabled, show a unified coverage warning modal.
          setCoverageDataRef(coverage);
          setShowCoverageWarningModal(true);

          return;
        }
        // Coverage data is NOT removed here — it's cleaned up only after updateWorkflow succeeds
      }
    }

    proceedAfterCoverage();
  }, [workflowStatus, workflowData, proceedAfterCoverage]);

  useEffect(() => {
    if (
      validationResults?.length > 0 &&
      aiQCTrigger &&
      shouldAutoRender.current
    ) {
      setOpenQcDrawer(true);

      shouldAutoRender.current = false;
    }
  }, [validationResults, workflowStatus, isApplicant, aiQCTrigger, setOpenQcDrawer]);

  useEffect(() => {
    const onSpeakerVerified = () => {
      const event = new CustomEvent("closeSpeakerVerification");

      dispatchEvent(event);

      setSpeakerVerified(true);
    };

    window.addEventListener("speakerVerificationComplete", onSpeakerVerified);

    return () => {
      window.removeEventListener("speakerVerificationComplete", onSpeakerVerified);
    }
  }, []);

  // Listen for AI QC complete event
  useEffect(() => {
    const onAIQCComplete = () => {
      onCompleteCheck();
    };

    window.addEventListener("editor.aiqc.complete", onAIQCComplete);

    return () => {
      window.removeEventListener("editor.aiqc.complete", onAIQCComplete);
    }
  }, [onCompleteCheck]);

  // Listen for AI QC recheck event
  useEffect(() => {
    const onAIQCRecheck = () => {
      checkTranscriptQuality();
    };

    window.addEventListener("editor.aiqc.recheck", onAIQCRecheck);

    return () => {
      window.removeEventListener("editor.aiqc.recheck", onAIQCRecheck);
    };
  }, [checkTranscriptQuality]);

  useEffect(() => {
    // This is for auto-triggering after automatic speaker validation
    if (speakerVerified && !qcTriggered) {
      setQCTriggered(() => true);
      onCompleteClick();
    }
  }, [speakerVerified, qcTriggered, onCompleteClick]);

  // Clean up coverage data only after workflow update succeeds
  useEffect(() => {
    if (isSaved && workflowId && workflowStatus) {
      const userInfo = getUserInfo();
      removeCoverage(workflowId, userInfo?.user?.localId, workflowStatus);
    }
  }, [isSaved, workflowId, workflowStatus]);

  return (
    <>
      {validationResults && aiQCTrigger && aiQCLimit < MAX_AI_QC_ATTEMPTS && (
        <LoadingButton
          disableElevation
          loading={qcLoading}
          loadingIndicator={
            <CircularProgress
              size={14}
              thickness={5}
              sx={{ color: isDarkMode ? "#E5E7EB" : "#6B7280" }}
            />
          }
          sx={{
            fontSize: "14px",
            padding: "10px 16px",
            color: isDarkMode ? "#FFF" : "#121212",
            border: "1px solid " + (isDarkMode ? "#494949" : "#E3E6EA"),
            "&.Mui-disabled": {
              color: isDarkMode ? "#7A7F89" : "#CACFD5",
              borderColor: isDarkMode ? "#2F3338" : "#777777",
            },
            "&:hover": {
              background: isDarkMode ? "#0A1E46" : "#E5ECF9",
              color: isDarkMode ? "#78A6FF" : "#0659F9",
              borderColor: isDarkMode ? "#1F4DA5" : "#0659F9",
            },
          }}
          variant="outlined"
          onClick={checkTranscriptQuality}
        >
          <RefreshOutlined
            sx={{
              color: "gray.400",
              fontSize: "16px",
              mr: "6px",
              ml: "-4px",
            }}
          />
          Recheck
        </LoadingButton>
      )}

      <LoadingButton
        disableElevation
        disabled={readOnly || isSaving || (workflowStatus === "Publication")}
        loading={qcLoading || isSaving}
        sx={{
          fontSize: "14px",
          padding: "10px 16px",
          color: "#FFF",
          backgroundColor: "#0659F9",
          "&:disabled": {
            color: "#CACFD5",
            backgroundColor: "#777777",
          },
        }}
        variant="contained"
        onClick={onCompleteCheck}
      >
        {workflowStatus === "Publication" ? "Completed" : "Complete"}
      </LoadingButton>

      <CheckInProgressModal loading={qcLoading} />
      <SaveWorkflowModal open={openSaveModal} setOpen={setOpenSaveModal} />

      {/* Coverage warning (audio playback, scroll depth etc.) modal */}
      <CoverageWarningModal
        open={showCoverageWarningModal}
        onClose={() => {
          setShowCoverageWarningModal(false);
          setCoverageDataRef(null);
        }}
        onSubmit={onCoverageWarningSubmit}
      />

      {/* Paragraph integrity check failure modal */}
      <IntegrityFailureModal open={showIntegrityFailureModal} />
    </>
  );
}
